import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Loader2, Download, TrendingUp, Clock, DollarSign, Zap, AlertCircle } from 'lucide-react';
import { assessmentAPI, leadAPI } from '../lib/api';
import { toast } from 'sonner';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function AssessmentResults() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: '', email: '', company: '', phone: '' });
  
  const loadResults = useCallback(async () => {
    try {
      const result = await assessmentAPI.get(id);
      setData(result);
    } catch (error) {
      console.error('Load results error:', error);
      toast.error('Failed to load results');
    } finally {
      setLoading(false);
    }
  }, [id]);
  
  useEffect(() => {
    loadResults();
  }, [loadResults]);
  
  const handleLeadSubmit = async () => {
    try {
      await leadAPI.create({
        ...leadData,
        source: 'assessment',
        assessment_id: id,
        status: 'new'
      });
      toast.success('Thank you! We will contact you soon.');
      setShowLeadForm(false);
    } catch (error) {
      console.error('Lead submission error:', error);
      toast.error('Failed to submit. Please try again.');
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--ap-blue)' }} />
      </div>
    );
  }
  
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
          <h2 className="text-xl font-semibold mb-2">Results Not Found</h2>
          <Link to="/assessment">
            <Button>Take New Assessment</Button>
          </Link>
        </div>
      </div>
    );
  }
  
  const { roi_metrics, insights, opportunity_score } = data;
  
  const savingsData = Array.from({ length: 12 }, (_, i) => ({
    month: `M${i + 1}`,
    savings: Math.round((roi_metrics.monthly_cost_savings * (i + 1)))
  }));
  
  const opportunitiesData = insights.top_opportunities.slice(0, 5).map(opp => ({
    name: opp.area,
    impact: opp.estimated_time_savings_percent
  }));
  
  return (
    <div className="min-h-screen flex flex-col">
      <TopNav />
      
      <main className="flex-grow">
        <div className="bg-gradient-to-b from-muted/50 to-background py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <Badge className="mb-4" variant="secondary">Your Automation Report</Badge>
              <h1 className="text-4xl font-semibold tracking-tight mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Here's What Automation Could Do For You
              </h1>
              <p className="text-muted-foreground max-w-2xl mx-auto">{insights.executive_summary}</p>
            </div>
            
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Automation Opportunity Score</CardTitle>
                  <div className="text-5xl font-bold tabular-nums" style={{ color: 'var(--ap-blue)' }}>
                    {opportunity_score}
                    <span className="text-xl text-muted-foreground">/100</span>
                  </div>
                </div>
              </CardHeader>
            </Card>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="rounded-xl" data-testid="kpi-total-savings-value">
                <CardHeader>
                  <DollarSign className="h-8 w-8 mb-2" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-sm font-medium text-muted-foreground">Annual Cost Savings</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold tabular-nums">${(roi_metrics.annual_cost_savings / 1000).toFixed(0)}K</div>
                  <p className="text-xs text-muted-foreground mt-1">${roi_metrics.monthly_cost_savings.toLocaleString()}/month</p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl">
                <CardHeader>
                  <Clock className="h-8 w-8 mb-2" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-sm font-medium text-muted-foreground">Hours Saved/Week</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold tabular-nums">{roi_metrics.hours_saved_per_week}</div>
                  <p className="text-xs text-muted-foreground mt-1">{roi_metrics.hours_saved_annually.toLocaleString()} annually</p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl">
                <CardHeader>
                  <TrendingUp className="h-8 w-8 mb-2" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-sm font-medium text-muted-foreground">Throughput Increase</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold tabular-nums">{roi_metrics.throughput_increase_percent}%</div>
                  <p className="text-xs text-muted-foreground mt-1">{roi_metrics.speed_multiplier}x faster</p>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl">
                <CardHeader>
                  <Zap className="h-8 w-8 mb-2" style={{ color: 'var(--ap-blue)' }} />
                  <CardTitle className="text-sm font-medium text-muted-foreground">Payback Period</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-semibold tabular-nums">{roi_metrics.payback_period_months}</div>
                  <p className="text-xs text-muted-foreground mt-1">months</p>
                </CardContent>
              </Card>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              <Card className="rounded-xl">
                <CardHeader>
                  <CardTitle>Projected Savings Over 12 Months</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={savingsData}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Area type="monotone" dataKey="savings" stroke="var(--ap-blue)" fill="var(--ap-blue)" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              
              <Card className="rounded-xl">
                <CardHeader>
                  <CardTitle>Top Automation Opportunities</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={opportunitiesData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="name" width={120} />
                      <Tooltip />
                      <Bar dataKey="impact" fill="var(--ap-blue)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
            
            <Card className="rounded-xl mb-8">
              <CardHeader>
                <CardTitle>Recommended Next Steps</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {insights.industry_specific_recommendations.map((rec) => (
                  <div key={rec} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-sm font-medium" style={{ color: 'var(--ap-blue)' }}>
                      {insights.industry_specific_recommendations.indexOf(rec) + 1}
                    </div>
                    <p className="text-sm flex-grow">{rec}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
            
            {!showLeadForm ? (
              <div className="text-center">
                <Card className="rounded-xl bg-muted/50 inline-block">
                  <CardContent className="pt-6">
                    <h3 className="text-xl font-semibold mb-2">Want to discuss these results?</h3>
                    <p className="text-sm text-muted-foreground mb-4">Our team can help you implement these automation opportunities.</p>
                    <Button onClick={() => setShowLeadForm(true)} data-testid="assessment-results-download-report-button">
                      <Download className="mr-2 h-4 w-4" />
                      Get Full Report & Consultation
                    </Button>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card className="rounded-xl max-w-md mx-auto">
                <CardHeader>
                  <CardTitle>Get Your Full Report</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Name</label>
                    <input
                      className="w-full px-3 py-2 border rounded-lg"
                      value={leadData.name}
                      onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Email</label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border rounded-lg"
                      value={leadData.email}
                      onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                      data-testid="assessment-results-email-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Company</label>
                    <input
                      className="w-full px-3 py-2 border rounded-lg"
                      value={leadData.company}
                      onChange={(e) => setLeadData({ ...leadData, company: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Phone (optional)</label>
                    <input
                      className="w-full px-3 py-2 border rounded-lg"
                      value={leadData.phone}
                      onChange={(e) => setLeadData({ ...leadData, phone: e.target.value })}
                    />
                  </div>
                  <Button onClick={handleLeadSubmit} className="w-full" data-testid="assessment-results-confirm-download-button">
                    Submit & Get Report
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}